"use client";

import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import FileUploadRoundedIcon from "@mui/icons-material/FileUploadRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import CloudDownloadRoundedIcon from "@mui/icons-material/CloudDownloadRounded";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";

import { useWorkspaceConfig } from "../../context/WorkspaceConfigContext";
import { useTranslation } from "react-i18next";

/** Helper */
function getSelectedCompanyIds() {
  try {
    const raw = sessionStorage.getItem("selectedCompanies");
    const arr = JSON.parse(raw || "[]");
    return Array.isArray(arr) ? arr.filter((n) => Number.isFinite(Number(n))).map(Number) : [];
  } catch {
    return [];
  }
}

const ProductsToolbar = ({
  onPickFile,
  onPickStockFile,
  onPickPriceFile,
  onPickRelatedFile,
  showImportPassagensErp = false,
  onImportPassagensErp,
  onImportCentral,
  importCentralLoading,
  onBulkCentralSync,
  bulkCentralLoading,
  hasLinkedWorkspaces = false,
  onAuditLinked,
  auditLinkedLoading = false,
  onOpenPaste,
  onCreateNew,
  canUpdateStock = true,
  canUpdatePrice = true,
  onExportExcel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation("products");
  const { isLoading, isError, byKey } = useWorkspaceConfig();

  const selectedCompanyIds = getSelectedCompanyIds();
  const multipleCompaniesSelected = selectedCompanyIds.length > 1;

  const cadastrocentral = byKey("cadastrocentral");
  /** Bloqueia ações em lote / criar produto; exportar Excel continua disponível no menu Ações. */
  const disabledByScope = multipleCompaniesSelected;

  const importInputRef = useRef(null);
  const stockInputRef = useRef(null);
  const priceInputRef = useRef(null);

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const [relatedDialogOpen, setRelatedDialogOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const relatedDialogInputRef = useRef(null);

  if (isLoading) return null;
  if (isError) return <div>{t("toolbar.configLoadError")}</div>;

  const btnSx = {
    height: 40,
    borderRadius: 2,
    textTransform: "none",
    whiteSpace: "nowrap",
  };

  const wrapperSx = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 1,
    width: isMobile ? "100%" : "auto",
    flexWrap: "nowrap", // <- impede quebrar feio
  };

  const openMenu = (e) => setAnchorEl(e.currentTarget);
  const closeMenu = () => setAnchorEl(null);

  const newProductDisabledHint = disabledByScope
    ? t("toolbar.selectOneCompanyForNewProduct")
    : "";

  const downloadRelatedTemplate = () => {
    try {
      const header = "SKU_Principal,SKU_Relacionado\n";
      const example = "12345ABC,67890DEF\n";
      const csv = header + example;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "modelo_relacionados.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      // falha silenciosa; o usuário ainda pode montar a planilha manualmente
    }
  };

  const acceptRelatedTypes = ".xlsx,.xls,.csv";
  const handleRelatedFile = (file) => {
    if (!file || !onPickRelatedFile) return;
    const syntheticEvent = { target: { files: [file] } };
    onPickRelatedFile(syntheticEvent);
    setRelatedDialogOpen(false);
  };

  const onRelatedInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleRelatedFile(file);
    e.target.value = "";
  };

  const onRelatedDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleRelatedFile(file);
  };

  const onRelatedDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const onRelatedDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  return (
    <Box sx={wrapperSx}>
      {/* INPUTS ocultos (disparados via menu) */}
      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        onChange={onPickFile}
        disabled={disabledByScope}
      />
      <input
        ref={stockInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        onChange={onPickStockFile}
        disabled={disabledByScope || !canUpdateStock}
      />
      <input
        ref={priceInputRef}
        type="file"
        accept=".xlsx,.xls,.csv"
        hidden
        onChange={onPickPriceFile}
        disabled={disabledByScope || !canUpdatePrice}
      />
      {/* Botão Ações — sempre abre o menu; com vários projetos só exportação Excel fica ativa */}
      <Button
        variant="outlined"
        startIcon={<MoreVertRoundedIcon />}
        onClick={openMenu}
        fullWidth={isMobile}
        sx={btnSx}
      >
        {t("toolbar.actions")}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={closeMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 280,
            mt: 0.5,
          },
        }}
      >
        {/* Grupo: Cadastrar produtos */}
        <MenuItem disabled dense sx={{ opacity: 1, cursor: "default" }}>
          <Typography variant="caption" color="text.secondary">
            {t("toolbar.groupRegister")}
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            importInputRef.current?.click();
          }}
          disabled={disabledByScope}
        >
          <ListItemIcon>
            <FileUploadRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("toolbar.importSpreadsheet")}
            secondary={t("toolbar.importSpreadsheetHint")}
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            onOpenPaste?.();
          }}
          disabled={disabledByScope}
        >
          <ListItemIcon>
            <ContentPasteRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("toolbar.pasteList")}
            secondary={t("toolbar.pasteListHint")}
          />
        </MenuItem>

        {showImportPassagensErp ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              onImportPassagensErp?.();
            }}
            disabled={disabledByScope}
          >
            <ListItemIcon>
              <CloudDownloadRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("toolbar.importPassagensErp")}
              secondary={t("toolbar.importPassagensErpHint")}
            />
          </MenuItem>
        ) : null}

        {cadastrocentral ? (
          <MenuItem
            onClick={() => {
              closeMenu();
              onImportCentral?.();
            }}
            disabled={disabledByScope || importCentralLoading}
          >
            <ListItemIcon>
              <CloudDownloadRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("toolbar.syncCentral")}
              secondary={
                importCentralLoading
                  ? t("toolbar.processing")
                  : t("toolbar.syncCentralHint")
              }
            />
          </MenuItem>
        ) : null}

        {onBulkCentralSync && hasLinkedWorkspaces && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onBulkCentralSync?.();
            }}
            disabled={disabledByScope || bulkCentralLoading}
          >
            <ListItemIcon>
              <CloudDownloadRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("toolbar.updateLinkedWs")}
              secondary={
                bulkCentralLoading
                  ? t("toolbar.updatingLinkedWs")
                  : t("toolbar.updateLinkedWsHint")
              }
            />
          </MenuItem>
        )}

        {onAuditLinked && hasLinkedWorkspaces && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onAuditLinked?.();
            }}
            disabled={disabledByScope || auditLinkedLoading}
          >
            <ListItemIcon>
              <AttachMoneyRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("toolbar.auditPrices")}
              secondary={
                auditLinkedLoading
                  ? t("toolbar.auditingPrices")
                  : t("toolbar.auditPricesHint")
              }
            />
          </MenuItem>
        )}

        <Divider sx={{ my: 0.75 }} />

        {/* Grupo: Atualizar cadastro em massa */}
        <MenuItem disabled dense sx={{ opacity: 1, cursor: "default" }}>
          <Typography variant="caption" color="text.secondary">
            {t("toolbar.bulkUpdate")}
          </Typography>
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            stockInputRef.current?.click();
          }}
          disabled={disabledByScope || !canUpdateStock}
        >
          <ListItemIcon>
            <Inventory2RoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("toolbar.updateStock")}
            secondary={
              canUpdateStock
                ? t("toolbar.updateStockHint")
                : t("toolbar.unavailable")
            }
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            priceInputRef.current?.click();
          }}
          disabled={disabledByScope || !canUpdatePrice}
        >
          <ListItemIcon>
            <AttachMoneyRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("toolbar.updatePrices")}
            secondary={
              canUpdatePrice
                ? t("toolbar.updatePricesHint")
                : t("toolbar.unavailable")
            }
          />
        </MenuItem>

        <MenuItem
          onClick={() => {
            closeMenu();
            setRelatedDialogOpen(true);
          }}
          disabled={disabledByScope || !onPickRelatedFile}
        >
          <ListItemIcon>
            <LinkRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={t("toolbar.updateRelated")}
            secondary={t("toolbar.updateRelatedHint")}
          />
        </MenuItem>

        <Divider sx={{ my: 0.75 }} />

        {/* Grupo: Exportar dados */}
        <MenuItem disabled dense sx={{ opacity: 1, cursor: "default" }}>
          <Typography variant="caption" color="text.secondary">
            {t("toolbar.exportData")}
          </Typography>
        </MenuItem>

        {onExportExcel && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onExportExcel();
            }}
            disabled={selectedCompanyIds.length === 0}
          >
            <ListItemIcon>
              <DownloadRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("toolbar.exportExcel")}
              secondary={t("toolbar.exportExcelHint")}
            />
          </MenuItem>
        )}
      </Menu>

      {/* Popup Importar relacionados */}
      <Dialog
        open={relatedDialogOpen}
        onClose={() => setRelatedDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>{t("toolbar.relatedDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("toolbar.relatedIntro")}
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 2 }}>
            <li>
              <Typography variant="body2"><strong>SKU_Principal</strong> — {t("toolbar.relatedColMain")}</Typography>
            </li>
            <li>
              <Typography variant="body2"><strong>SKU_Relacionado</strong> — {t("toolbar.relatedColRelated")}</Typography>
            </li>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("toolbar.relatedRowHint")}
          </Typography>
          <Button
            size="small"
            startIcon={<DownloadRoundedIcon />}
            onClick={downloadRelatedTemplate}
            sx={{ mb: 2 }}
          >
            {t("toolbar.relatedTemplate")}
          </Button>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t("toolbar.relatedDropHint")}
          </Typography>
          <Box
            onClick={() => relatedDialogInputRef.current?.click()}
            onDrop={onRelatedDrop}
            onDragOver={onRelatedDragOver}
            onDragLeave={onRelatedDragLeave}
            sx={{
              border: "2px dashed",
              borderColor: dragOver ? "primary.main" : "divider",
              borderRadius: 2,
              py: 3,
              px: 2,
              textAlign: "center",
              cursor: "pointer",
              bgcolor: dragOver ? "action.hover" : "transparent",
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <input
              ref={relatedDialogInputRef}
              type="file"
              accept={acceptRelatedTypes}
              hidden
              onChange={onRelatedInputChange}
            />
            <FileUploadRoundedIcon sx={{ fontSize: 40, color: "text.secondary", mb: 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {t("toolbar.relatedDropZone")}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* CTA principal */}
      {onCreateNew && (
        <Tooltip title={newProductDisabledHint} disableHoverListener={!disabledByScope}>
          <span>
            <Button
              startIcon={<AddRoundedIcon />}
              variant="contained"
              color="primary"
              onClick={onCreateNew}
              fullWidth={isMobile}
              disabled={disabledByScope}
              sx={{
                ...btnSx,
                boxShadow: "none",
                px: 2,
              }}
            >
              {t("toolbar.newProduct")}
            </Button>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export default ProductsToolbar;
